/**
 * Views Dashboard Card
 * 
 * A dashboard card component that shows recent views and quick actions
 * for Views functionality. Integrates into the main Dashboard.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  ViewModule as ViewsIcon,
  ArrowForward,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Drafts as DraftIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { viewsApiService } from '../../services/views.api';
import { View } from '../../types/view.types';
import { EmptyState } from '../common/EmptyState';

interface ViewsDashboardCardProps {
  maxItems?: number;
}

export const ViewsDashboardCard: React.FC<ViewsDashboardCardProps> = ({
  maxItems = 5,
}) => {
  const navigate = useNavigate();
  const { teams, currentTeamId, setCurrentTeamId } = useOrganization();
  
  const [views, setViews] = useState<View[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get team context for Views (team-specific only)
  const currentTeam = teams.find(team => team.id === currentTeamId);
  const teamId = currentTeam?.id;
  const organizationId = currentTeam?.organizationId;

  useEffect(() => {
    if (teamId) {
      loadRecentViews();
    } else {
      setLoading(false);
    }
  }, [teamId]);

  const loadRecentViews = async () => {
    if (!teamId || !organizationId) return;
    
    setLoading(true);
    try {
      const response = await viewsApiService.listViews({
        organization_id: organizationId,
        team_id: teamId,
        limit: maxItems,
        sort_by: 'updatedAt',
        sort_order: 'desc',
      });
      // Ensure response.data is an array before setting
      setViews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error loading recent views:', err);
      setError('Failed to load views');
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = async (view: View) => {
    // Set team context if needed
    if (view.teamId && view.teamId !== teamId) {
      await setCurrentTeamId(view.teamId);
    }
    navigate(`/views/${view.id}`);
  };

  const getStatusIcon = (view: View) => {
    const status = view.status;
    switch (status) {
      case 'draft':
        return <DraftIcon fontSize="small" color="disabled" />;
      case 'private':
        return <PrivateIcon fontSize="small" color="action" />;
      case 'public':
        return <PublicIcon fontSize="small" color="success" />;
      case 'archived':
        return <ArchiveIcon fontSize="small" color="warning" />;
      default:
        return <PrivateIcon fontSize="small" color="action" />;
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        boxShadow: 'none',
        '&:hover': { transform: 'none', boxShadow: 'none' }
      }}
    >
      <CardHeader
        avatar={<ViewsIcon color="primary" />}
        title="Recent Views"
        subheader={teamId ? `Latest curated content experiences for ${currentTeam?.name}` : 'Select a team to view content'}
        action={
          teamId && (
            <Button
              component={Link}
              to="/views/create"
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
            >
              New view
            </Button>
          )
        }
      />
      <Divider />
      <CardContent sx={{ pt: 1 }}>
        {!teamId ? (
          <EmptyState
            icon={<ViewsIcon sx={{ fontSize: 48 }} />}
            title="No team selected"
            description="Select a team to view and create curated content experiences."
          />
        ) : loading ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton width="60%" height={24} />
                  <Skeleton width="40%" height={20} />
                </Box>
              </Box>
            ))}
          </Stack>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <ViewsIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" color="error" gutterBottom>
              Error Loading Views
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Button variant="outlined" onClick={loadRecentViews}>
              Retry
            </Button>
          </Box>
        ) : views.length > 0 ? (
          <List>
            {views.slice(0, maxItems).map((view, index) => (
              <React.Fragment key={view.id}>
                <ListItem
                  component="div"
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  }}
                  onClick={() => handleViewClick(view)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {view.name}
                        </Typography>
                        {getStatusIcon(view)}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" noWrap component="span" display="block">
                          {view.description || 'No description'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" component="span" display="block">
                          Updated {new Date(view.updatedAt).toLocaleDateString()}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewClick(view);
                      }}
                      size="small"
                      endIcon={<ArrowForward />}
                    >
                      Open
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < views.slice(0, maxItems).length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <EmptyState
            icon={<ViewsIcon sx={{ fontSize: 48 }} />}
            title="No views yet"
            description="Create your first curated content experience to get started."
            actionLabel="Create view"
            actionTo="/views/create"
          />
        )}

        {views.length > maxItems && (
          <Box sx={{ pt: 2, textAlign: 'center' }}>
            <Button
              component={Link}
              to="/views"
              endIcon={<ArrowForward />}
            >
              View All Views ({views.length})
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ViewsDashboardCard;